import { useMemo } from 'react';

import {
   Bean,
   commonTranslationKeys,
   createSingleInterpolatedTranslationKey,
   NumberPatternType,
   ValidationRules,
} from '@dreryk/react-common';

import {
   getPreciseDosagePortionMaxValue,
   getPreciseDosagePortionMinValue,
   getPreciseDosagePortionPrecision,
   getPreciseDosagePortionStep,
} from '~libs.shared.prescription';

import { prescriptionTranslationKeys } from '../../../../../translations';
import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';
import { DraftStandardPrescriptionFieldName } from '../../draftPrescriptions/standardPrescription/DraftStandardPrescriptionField.types';

import { StyledQuantityNumberInput } from './QuantityNumberInput.styled';
import { getFormattedQuantityLimit } from './QuantityNumberInput.utils';

type QuantityNumberInputProps = Pick<DraftPrescriptionsFormController, 'register'> & {
   readonly name: DraftStandardPrescriptionFieldName;
   readonly defaultValue: number | null;
   readonly required: boolean;
};

export const QuantityNumberInput = (props: QuantityNumberInputProps): JSX.Element => {
   const validationRules = useMemo<ValidationRules<Bean>>(
      () => ({
         required: props.required,
         min: {
            value: getPreciseDosagePortionMinValue(),
            message: createSingleInterpolatedTranslationKey(
               commonTranslationKeys.validation.minLimit,
               'limit',
               getFormattedQuantityLimit(getPreciseDosagePortionMinValue())
            ),
         },
         max: {
            value: getPreciseDosagePortionMaxValue(),
            message: createSingleInterpolatedTranslationKey(
               commonTranslationKeys.validation.maxLimit,
               'limit',
               getFormattedQuantityLimit(getPreciseDosagePortionMaxValue())
            ),
         },
      }),
      [props.required]
   );

   return (
      <StyledQuantityNumberInput
         testId={'numericalDosageQuantity'}
         controller={props.register(props.name, validationRules)}
         placeholder={prescriptionTranslationKeys.numericalDosage.drugQuantity}
         defaultValue={props.defaultValue}
         stepValue={getPreciseDosagePortionStep()}
         precision={getPreciseDosagePortionPrecision()}
         min={getPreciseDosagePortionMinValue()}
         max={getPreciseDosagePortionMaxValue()}
         pattern={NumberPatternType.POSITIVE_FLOAT}
         autoComplete={false}
         compact
      />
   );
};
